import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';
import { parseSDGNumbers } from './services/sdgMapper';

interface Bond {
    _id: string;
    isin: string;
    update_timestamp: string;
    filename?: string[];
    report_id: string[];
    validation_id: string[];
    issue_date: string;
    maturity_date: string;
    volume: string;
    currency: string;
    duration: string; // Original duration field - kept as comment
    duration_days?: number;
    volume_as_number: number;
    currency_fixed: string;
    volume_has_a_range: boolean;
    volume_min_as_number: number;
    report_source_id: string[];
    use_of_proceeds: string;
    issuer: string;
    coupon?: string;
    rating?: string;
    framework?: string;
    verifier?: string;
    stock_exchange?: string;
    tags?: string[];
    bond_type?: string; // Bond classification: Green, Social, Sustainability, Blue, etc.
    ground_truth_validation?: {
        validated_at: Date | string;
        ground_truth_id?: string;
        status: string;
        message?: string;
        accuracy_score?: number;
        has_conflicts?: boolean;
        mismatched_fields?: string[];
        field_validations?: {
            [key: string]: {
                bonds_value: any;
                gt_value: any;
                match: boolean;
                has_value: boolean;
            };
        };
        isin_validation?: {
            valid: boolean;
            errors?: string[];
            warnings?: string[];
        };
    };
    ground_truth_cross_referenced_at?: Date | string;
    isin_validation_updated_at?: Date | string;
    report_sources?: any[]; // Array of report source documents included in response
    contribution_to_SDG?: string;
    sdg_numbers?: number[];
    projects?: Array<{
        name: string;
        type: string;
        location: string;
        funds_allocation: number;
        allocation_currency: string;
    }>;
    funds_allocation?: number;
    funds_allocation_currency?: string;
    ghg_emissions_avoided?: number;
    ghg_emissions_avoided_unit?: string;
    energy_saved?: string;
    energy_saved_unit?: string;
    optimization?: {
        optimized: boolean;
        optimized_at: Date | string;
        optimization_version: string;
        optimization_source: string;
        parsed_fields: string[];
        parsed_fields_count: number;
        optimization_count: number;
        previous_optimized_at?: Date | string;
    };
    optimization_history?: Array<{
        optimized: boolean;
        optimized_at: Date | string;
        optimization_version: string;
        optimization_source: string;
        parsed_fields: string[];
        parsed_fields_count: number;
        optimization_count: number;
        previous_optimized_at?: Date | string;
    }>;
    identifier_type?: string;
    identifier_value?: string;
    classification_results?: {
        plan?: string;
        category?: string[];
        class_probabilities?: number[];
        short_description?: string;
        reasoning?: string[];
    };
}

const BondDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token, authLoaded, user, isAuthenticated } = useAuth();
    const { t } = useTranslation();

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const storageUrl = env.storageUrl;

    const [bond, setBond] = useState<Bond | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [relatedDocuments, setRelatedDocuments] = useState<any[]>([]);
    const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
    const [openReportDropdownIndex, setOpenReportDropdownIndex] = useState<number | null>(null);

    // Map SDG numbers to SDG objects for display
    const sdgMappings = bond?.sdg_numbers ? parseSDGNumbers(bond.sdg_numbers) : [];

    useEffect(() => {
        if (!authLoaded) return;
        fetchBondDetails();
    }, [id, authLoaded, token]);

    const fetchBondDetails = async () => {
        if (!id) {
            setError('No bond ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const headers: Record<string, string> = {
                'x-api-key': apiKey
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Fetch bond details
            const response = await axios.get<{ bond: Bond }>(`${apiUrl}/bonds/${id}`, { headers });
            console.log('Bond response:', response.data);
            console.log('Report sources included:', response.data.bond.report_sources);
            setBond(response.data.bond);

            // Use report sources directly from the bond response and filter duplicates
            if (response.data.bond.report_sources && response.data.bond.report_sources.length > 0) {
                // Filter duplicates by _id
                const uniqueDocs = response.data.bond.report_sources.filter((doc: any, index: number, self: any[]) =>
                    index === self.findIndex((d) => d._id === doc._id)
                );
                setRelatedDocuments(uniqueDocs);
                console.log(`Set ${uniqueDocs.length} unique report sources from ${response.data.bond.report_sources.length} total`);
            } else {
                console.log('No report sources found in bond response');
            }

            setError(null);
        } catch (err: any) {
            console.error('Error fetching bond details:', err);
            if (err.response?.status === 404) {
                setError(t('bonds.notFound'));
            } else if (err.response?.status === 403) {
                setError(t('bonds.accessDenied'));
            } else {
                setError(t('bonds.loadError'));
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateValue: string | Date) => {
        if (!dateValue) {
            return 'N/A';
        }

        // Handle if it's already a Date object
        if (dateValue instanceof Date) {
            return dateValue.toLocaleDateString();
        }

        // Handle string dates
        if (typeof dateValue === 'string' && dateValue.trim() === '') {
            return 'N/A';
        }

        try {
            const date = new Date(dateValue as string);
            if (!isNaN(date.getTime())) {
                return new Intl.DateTimeFormat(navigator.language, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }).format(date);
            }
        } catch {
            console.warn(`Unable to parse date: "${dateValue}"`);
        }

        return String(dateValue);
    };

    const formatVolume = (volumeAsNumber: number | undefined, currency: string) => {
        if (volumeAsNumber === undefined || volumeAsNumber === null) {
            return 'N/A';
        }

        const volumeInMillions = volumeAsNumber / 1000000;
        const formattedVolume = volumeInMillions < 1
            ? volumeInMillions.toFixed(2)
            : volumeInMillions.toFixed(0);

        return `${currency} ${formattedVolume} Million`;
    };

    const formatDurationFromDays = (days: number | undefined) => {
        if (!days) return 'N/A';

        const years = days / 365;
        if (years < 1) {
            return `${days} ${t('bonds.fields.days')}`;
        }

        // Format to 1 decimal place if not a whole number
        const formattedYears = years % 1 === 0 ? years.toString() : years.toFixed(1);
        return `${formattedYears} ${t('bonds.fields.years')}`;
    };

    const getBondTypeColor = (bondType: string | undefined): string => {
        if (!bondType) return '#64748b'; // Gray for unknown

        const lowerType = bondType.toLowerCase();

        if (lowerType.includes('green')) return '#10b981'; // Green
        if (lowerType.includes('social')) return '#3b82f6'; // Blue
        if (lowerType.includes('sustainability')) return '#8b5cf6'; // Purple
        if (lowerType.includes('blue')) return '#06b6d4'; // Cyan
        if (lowerType.includes('transition')) return '#f59e0b'; // Amber

        return '#64748b'; // Default gray
    };

    const formatBondType = (bondType: string | undefined): string => {
        if (!bondType) return t('bonds.fields.bondTypeUnknown', 'Unknown');

        // Clean up the bond type string
        const cleanType = bondType.trim();

        // Normalize common variations
        if (cleanType.toLowerCase() === 'green bond' || cleanType.toLowerCase() === 'green') {
            return t('bonds.types.green', 'Green Bond');
        }
        if (cleanType.toLowerCase() === 'social bond' || cleanType.toLowerCase() === 'social') {
            return t('bonds.types.social', 'Social Bond');
        }
        if (cleanType.toLowerCase().includes('sustainability-linked')) {
            return t('bonds.types.sustainabilityLinked', 'Sustainability-Linked Bond');
        }
        if (cleanType.toLowerCase() === 'sustainability bond' || cleanType.toLowerCase() === 'sustainability') {
            return t('bonds.types.sustainability', 'Sustainability Bond');
        }
        if (cleanType.toLowerCase() === 'blue bond' || cleanType.toLowerCase() === 'blue') {
            return t('bonds.types.blue', 'Blue Bond');
        }
        if (cleanType.toLowerCase().includes('transition')) {
            return t('bonds.types.transition', 'Transition Bond');
        }

        // Return as-is if no match
        return cleanType;
    };

    const [searchParams] = useSearchParams();

    const handleBack = () => {
        // Preserve search and filter state when going back
        const params = searchParams.toString();
        navigate(`/bonds${params ? `?${params}` : ''}`);
    };

    const handleDocumentClick = (docId: string) => {
        navigate(`/report-sources?doc=${docId}&returnTo=/bonds/${id}`);
    };

    const toggleDropdown = (index: number) => {
        setOpenDropdownIndex(openDropdownIndex === index ? null : index);
    };

    const toggleReportDropdown = (index: number) => {
        setOpenReportDropdownIndex(openReportDropdownIndex === index ? null : index);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.document-dropdown')) {
                setOpenDropdownIndex(null);
            }
            if (!target.closest('.report-dropdown')) {
                setOpenReportDropdownIndex(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    if (!authLoaded || loading) {
        return (
            <div className="loading-container">
                <div className="bonds-loader-flex">
                    <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
                    <span className="loader-text">{t('loading.bondDetails')}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px', color: '#e74c3c' }}></i>
                    {error}
                </div>
                <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
                    <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
                    {t('buttons.backToBonds')}
                </button>
            </div>
        );
    }

    if (!bond) {
        return (
            <div className="error-container">
                <div className="error-message">{t('bonds.notFound')}</div>
                <button onClick={handleBack} className="btn-primary" style={{ marginTop: '20px' }}>
                    <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
                    {t('buttons.backToBonds')}
                </button>
            </div>
        );
    }

    return (
        <div className="bond-detail-page" style={{ width: '100%', maxWidth: '100%', padding: '0 20px', backgroundColor: '#f5f6f8', boxSizing: 'border-box' }}>
            <div className="bond-detail-header">
                <div className="bond-detail-nav">
                    <button onClick={handleBack} className="back-button">
                        <i className="fas fa-arrow-left"></i>
                        {t('buttons.back')}
                    </button>
                </div>
                <h2 className="bond-detail-title">{t('bonds.detailTitle')}</h2>
            </div>

            <div style={{ width: '100%', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box' }}>
                <div style={{ padding: '0' }}>
                    {/* Combined Overview + Financial Summary */}
                    <div className="detail-section combined-overview-summary" style={{ backgroundColor: '#ffffff' }}>
                        <div className="combined-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                            {/* Overview (left) with two inner columns */}
                            <div className="overview-section">
                                <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '12px', borderBottom: 'none', paddingBottom: 0 }}>Bond Overview</h3>
                                <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                                    <div className="detail-item">
                                        <div className="detail-label-small">ISIN</div>
                                        <div className="detail-value-large" style={{ fontWeight: 'bold' }}>{bond.isin}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label-small">Issuer</div>
                                        <div className="detail-value-large">{bond.issuer || 'N/A'}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label-small">Issue Date</div>
                                        <div className="detail-value-large">{formatDate(bond.issue_date)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label-small">Maturity Date</div>
                                        <div className="detail-value-large">{formatDate(bond.maturity_date)}</div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label-small">Duration</div>
                                        <div className="detail-value-large">
                                            {formatDurationFromDays(bond.duration_days)}
                                        </div>
                                    </div>
                                    <div className="detail-item">
                                        <div className="detail-label-small">{t('bonds.fields.bondType', 'Bond Type')}</div>
                                        <div className="detail-value-large" style={{
                                            color: getBondTypeColor(bond.bond_type),
                                            fontWeight: '600'
                                        }}>
                                            {formatBondType(bond.bond_type)}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Financial Summary (right) with left separator */}
                            <div className="financial-summary" style={{ paddingLeft: '24px' }}>
                                <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px', borderBottom: 'none', paddingBottom: 0 }}>Financial Summary</h3>
                                <div className="detail-item">
                                    <div className="detail-label-small">Volume</div>
                                    <div className="detail-value-large">
                                        {formatVolume(bond.volume_as_number, bond.currency || bond.currency_fixed || 'EUR')}
                                    </div>
                                </div>
                                {bond.coupon && (
                                    <div className="detail-item">
                                        <div className="detail-label-small">Coupon Rate</div>
                                        <div className="detail-value-large">{bond.coupon}</div>
                                    </div>
                                )}
                                {/* Optional future metrics kept commented */}
                            </div>
                        </div>
                    </div>

                    {/* Rating & Risk below spanning full width */}
                    {/*<div className="detail-section full-width">*/}
                    {/*    <h3>Rating & Risk</h3>*/}
                    {/*    {bond.rating && (*/}
                    {/*        <div className="detail-item">*/}
                    {/*            <div className="detail-label-small">Credit Rating</div>*/}
                    {/*            <div className="detail-value-large" style={{ fontWeight: 'bold' }}>{bond.rating}</div>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*    {bond.stock_exchange && (*/}
                    {/*        <div className="detail-item">*/}
                    {/*            <div className="detail-label-small">Exchange</div>*/}
                    {/*            <div className="detail-value-large">{bond.stock_exchange}</div>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*    {bond.verifier && (*/}
                    {/*        <div className="detail-item">*/}
                    {/*            <div className="detail-label-small">Verifier</div>*/}
                    {/*            <div className="detail-value-large">{bond.verifier}</div>*/}
                    {/*        </div>*/}
                    {/*    )}*/}
                    {/*</div>*/}

                    {isAuthenticated && bond.use_of_proceeds && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Use of Proceeds</h3>
                            <div className="detail-text" style={{ marginBottom: '16px' }}>{bond.use_of_proceeds}</div>

                            {bond.framework && (
                                <div style={{
                                    padding: '16px',
                                    backgroundColor: '#f0fdf4',
                                    borderRadius: '8px',
                                    border: '1px solid #bbf7d0'
                                }}>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#15803d', fontSize: '16px' }}>Green Bond Framework</h4>
                                    <div className="detail-text">{bond.framework}</div>

                                    {/* Display SDG badges if available */}
                                    {sdgMappings.length > 0 && (
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                                            {sdgMappings.map(sdg => (
                                                <span key={sdg.number} style={{
                                                    padding: '4px 12px',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#15803d',
                                                    borderRadius: '4px',
                                                    fontSize: '13px',
                                                    fontWeight: '500'
                                                }}>
                                                    {sdg.fullName}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {isAuthenticated && bond.verifier && (
                        <div className="detail-section" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.verification')}</h3>
                            <div className="detail-row" style={{ textAlign: 'left' }}>
                                <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.fields.verifier')}:</span>
                                <span className="detail-value" style={{ textAlign: 'left' }}>{bond.verifier}</span>
                            </div>
                        </div>
                    )}

                    {bond.filename && bond.filename.length > 0 && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.documents')}</h3>
                            <div className="document-list">
                                {bond.filename.map((file, index) => (
                                    <div key={index} className="document-item">
                                        <div className="document-info">
                                            <i className="fas fa-file-pdf"></i>
                                            <span className="document-name">{file.split('/').pop() || file}</span>
                                        </div>
                                        <div className="document-dropdown">
                                            <button
                                                className="document-dropdown-toggle"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleDropdown(index);
                                                }}
                                                aria-label="Document actions"
                                            >
                                                <i className="fas fa-ellipsis-v"></i>
                                            </button>
                                            {openDropdownIndex === index && (
                                                <div className="document-dropdown-menu">
                                                    <button
                                                        className="document-dropdown-item"
                                                        onClick={() => {
                                                            const pdfUrl = `${storageUrl}/${file}`;
                                                            window.open(pdfUrl, '_blank');
                                                            setOpenDropdownIndex(null);
                                                        }}
                                                        title="Open in new tab"
                                                    >
                                                        <i className="fas fa-external-link-alt"></i>
                                                        <span>{t('bonds.viewPDF', 'View PDF')}</span>
                                                    </button>
                                                    <button
                                                        className="document-dropdown-item"
                                                        onClick={() => {
                                                            const pdfUrl = `${storageUrl}/${file}`;
                                                            const link = document.createElement('a');
                                                            link.href = pdfUrl;
                                                            link.download = file.split('/').pop() || file;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                            setOpenDropdownIndex(null);
                                                        }}
                                                        title="Download document"
                                                    >
                                                        <i className="fas fa-download"></i>
                                                        <span>{t('bonds.downloadPDF', 'Download PDF')}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {isAuthenticated && relatedDocuments.length > 0 && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.reportSourcesTitle', 'Report Sources')}</h3>
                            <div className="report-sources-list">
                                <table className="report-sources-table">
                                    <thead>
                                        <tr>
                                            <th>{t('bonds.reportSources.title')}</th>
                                            <th>{t('bonds.reportSources.author')}</th>
                                            <th>{t('bonds.reportSources.date')}</th>
                                            <th>{t('bonds.reportSources.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {relatedDocuments.map((doc) => (
                                            <tr key={doc._id}>
                                                <td className="report-source-title">
                                                    <a
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleDocumentClick(doc._id);
                                                        }}
                                                        className="report-source-link"
                                                    >
                                                        {doc.metadata?.title || doc.title || 'Untitled'}
                                                    </a>
                                                </td>
                                                <td>{doc.metadata?.author || doc.author || 'N/A'}</td>
                                                <td>{doc.metadata?.date || doc.publication_date ? formatDate(doc.metadata?.date || doc.publication_date) : 'N/A'}</td>
                                                <td>
                                                    <div className="report-dropdown">
                                                        <button
                                                            className="document-dropdown-toggle"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleReportDropdown(relatedDocuments.findIndex(d => d._id === doc._id));
                                                            }}
                                                            aria-label="Report source actions"
                                                        >
                                                            <i className="fas fa-ellipsis-v"></i>
                                                        </button>
                                                        {openReportDropdownIndex === relatedDocuments.findIndex(d => d._id === doc._id) && (
                                                            <div className="document-dropdown-menu">
                                                                <button
                                                                    className="document-dropdown-item"
                                                                    onClick={() => {
                                                                        handleDocumentClick(doc._id);
                                                                        setOpenReportDropdownIndex(null);
                                                                    }}
                                                                    title="View in Report Sources"
                                                                >
                                                                    <i className="fas fa-eye"></i>
                                                                    <span>{t('bonds.view', 'View')}</span>
                                                                </button>
                                                                {doc.url && (
                                                                    <>
                                                                        <a
                                                                            className="document-dropdown-item"
                                                                            href={doc.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            title="Open in new tab"
                                                                        >
                                                                            <i className="fas fa-external-link-alt"></i>
                                                                            <span>{t('bonds.open', 'Open')}</span>
                                                                        </a>
                                                                        <a
                                                                            className="document-dropdown-item"
                                                                            href={doc.url}
                                                                            download
                                                                            title="Download document"
                                                                        >
                                                                            <i className="fas fa-download"></i>
                                                                            <span>{t('bonds.download', 'Download')}</span>
                                                                        </a>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Tags - Admin Only */}
                    {user?.role === 'ADMIN' && bond.tags && bond.tags.length > 0 && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.allTags')}</h3>
                            <div className="tags-container">
                                {bond.tags.map((tag, index) => (
                                    <span key={index} className="tag-badge">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Document Categories */}
                    {isAuthenticated && bond.classification_results?.category && bond.classification_results.category.length > 0 && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>Document Categories</h3>
                            <div className="projects-list">
                                <table className="projects-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Category</th>
                                            <th>Probability</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bond.classification_results.category.map((cat: string, index: number) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{cat}</td>
                                                <td>
                                                    {bond.classification_results?.class_probabilities?.[index]
                                                        ? `${(bond.classification_results.class_probabilities[index] * 100).toFixed(1)}%`
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* SDG Contribution - Dynamic Display */}
                    {isAuthenticated && (bond.sdg_numbers || bond.contribution_to_SDG) && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>
                                {t('bonds.sdgContribution')}
                            </h3>

                            {/* Display mapped SDGs from sdg_numbers */}
                            {sdgMappings.length > 0 && (
                                <div className="detail-row" style={{ marginBottom: '12px' }}>
                                    <span className="detail-label" style={{ fontWeight: '600' }}>
                                        {t('bonds.sdgGoals')}:
                                    </span>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '8px',
                                        marginTop: '8px'
                                    }}>
                                        {sdgMappings.map(sdg => (
                                            <span key={sdg.number} style={{
                                                padding: '4px 12px',
                                                backgroundColor: '#e3f2fd',
                                                color: '#1565c0',
                                                borderRadius: '4px',
                                                fontSize: '13px',
                                                fontWeight: '500'
                                            }}>
                                                {sdg.fullName}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Display original text description if exists */}
                            {bond.contribution_to_SDG && (
                                <div className="detail-row" style={{ textAlign: 'left', marginTop: sdgMappings.length > 0 ? '12px' : '0' }}>
                                    <span className="detail-label" style={{ fontWeight: '600' }}>
                                        {t('bonds.sdgDescription')}:
                                    </span>
                                    <span className="detail-value" style={{ textAlign: 'left', display: 'block', marginTop: '4px' }}>
                                        {bond.contribution_to_SDG}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Projects */}
                    {isAuthenticated && bond.projects && bond.projects.length > 0 && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.projectsTitle')}</h3>
                            <div className="projects-list">
                                <table className="projects-table">
                                    <thead>
                                        <tr>
                                            <th>{t('bonds.projects.name')}</th>
                                            <th>{t('bonds.projects.type')}</th>
                                            <th>{t('bonds.projects.location')}</th>
                                            <th>{t('bonds.projects.fundsAllocation')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bond.projects.map((project, index) => (
                                            <tr key={index}>
                                                <td className="project-name">{project.name}</td>
                                                <td>{project.type}</td>
                                                <td>{project.location}</td>
                                                <td className="funds-allocation">
                                                    {project.allocation_currency} {(project.funds_allocation / 1000000).toFixed(0)}M
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Environmental Impact */}
                    {isAuthenticated && (bond.ghg_emissions_avoided || bond.energy_saved || bond.funds_allocation) && (
                        <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.environmentalImpact')}</h3>
                            <div className="impact-metrics" style={{ textAlign: 'left' }}>
                                {bond.funds_allocation && (
                                    <div className="detail-row" style={{ textAlign: 'left' }}>
                                        <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.impact.totalFundsAllocation')}:</span>
                                        <span className="detail-value" style={{ textAlign: 'left' }}>
                                            {bond.funds_allocation_currency || bond.currency} {(bond.funds_allocation / 1000000).toFixed(0)}M
                                        </span>
                                    </div>
                                )}
                                {bond.ghg_emissions_avoided && (
                                    <div className="detail-row" style={{ textAlign: 'left' }}>
                                        <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.impact.ghgEmissionsAvoided')}:</span>
                                        <span className="detail-value" style={{ textAlign: 'left' }}>
                                            {bond.ghg_emissions_avoided.toLocaleString()} {bond.ghg_emissions_avoided_unit || 'tons CO₂'}
                                        </span>
                                    </div>
                                )}
                                {bond.energy_saved && (
                                    <div className="detail-row" style={{ textAlign: 'left' }}>
                                        <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.impact.energySaved')}:</span>
                                        <span className="detail-value" style={{ textAlign: 'left' }}>{bond.energy_saved}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Identifiers - Admin Only */}
                    {user?.role === 'ADMIN' && (bond.identifier_type || bond.identifier_value) && (
                        <div className="detail-section" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.identifiers')}</h3>
                            {bond.identifier_type && (
                                <div className="detail-row" style={{ textAlign: 'left' }}>
                                    <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.identifiers.type')}:</span>
                                    <span className="detail-value" style={{ textAlign: 'left' }}>{bond.identifier_type}</span>
                                </div>
                            )}
                            {bond.identifier_value && (
                                <div className="detail-row" style={{ textAlign: 'left' }}>
                                    <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.identifiers.value')}:</span>
                                    <span className="detail-value" style={{ textAlign: 'left' }}>{bond.identifier_value}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Validation Information - Admin Only */}
                    {user?.role === 'ADMIN' && bond.ground_truth_validation && (
                        <div className="detail-section full-width validation-section" style={{ backgroundColor: '#ffffff', marginTop: '24px' }}>
                            <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '16px' }}>{t('bonds.validationInformation')}</h3>

                            <div className="validation-overview" style={{ textAlign: 'left' }}>
                                <div className="validation-status" style={{
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '16px'
                                }}>
                                    <span className="detail-label" style={{ fontWeight: '500' }}>{t('bonds.validation.status', 'Validation Status')}:</span>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        backgroundColor: bond.ground_truth_validation.status === 'complete' ? '#dcfce7' :
                                            bond.ground_truth_validation.status === 'partial' ? '#fef3c7' :
                                                bond.ground_truth_validation.status === 'failed' ? '#fee2e2' : '#f3f4f6',
                                        color: bond.ground_truth_validation.status === 'complete' ? '#15803d' :
                                            bond.ground_truth_validation.status === 'partial' ? '#92400e' :
                                                bond.ground_truth_validation.status === 'failed' ? '#991b1b' : '#374151',
                                        border: `1px solid ${bond.ground_truth_validation.status === 'complete' ? '#bbf7d0' :
                                            bond.ground_truth_validation.status === 'partial' ? '#fde68a' :
                                                bond.ground_truth_validation.status === 'failed' ? '#fecaca' : '#e5e7eb'
                                            }`
                                    }}>
                                        {bond.ground_truth_validation.status === 'complete' && <i className="fas fa-check-circle" style={{ fontSize: '14px' }}></i>}
                                        {bond.ground_truth_validation.status === 'partial' && <i className="fas fa-exclamation-triangle" style={{ fontSize: '14px' }}></i>}
                                        {bond.ground_truth_validation.status === 'failed' && <i className="fas fa-times-circle" style={{ fontSize: '14px' }}></i>}
                                        {bond.ground_truth_validation.status.charAt(0).toUpperCase() + bond.ground_truth_validation.status.slice(1)}
                                    </span>
                                    {bond.ground_truth_validation.has_conflicts && (
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: '#fee2e2',
                                            color: '#991b1b',
                                            fontSize: '12px',
                                            fontWeight: '500'
                                        }}>
                                            <i className="fas fa-exclamation" style={{ fontSize: '10px' }}></i>
                                            Has Conflicts
                                        </span>
                                    )}
                                </div>

                                {bond.ground_truth_validation.accuracy_score !== undefined && (
                                    <div className="validation-score" style={{ marginTop: '16px', marginBottom: '16px' }}>
                                        <div className="accuracy-score-container" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                            <span className="accuracy-score-label" style={{ fontWeight: '500', minWidth: '120px' }}>{t('bonds.validation.accuracyScore', 'Accuracy Score')}:</span>
                                            <div className="accuracy-progress-bar" style={{
                                                flex: 1,
                                                height: '24px',
                                                backgroundColor: '#f3f4f6',
                                                borderRadius: '12px',
                                                overflow: 'hidden',
                                                position: 'relative'
                                            }}>
                                                <div
                                                    className="accuracy-progress-fill"
                                                    style={{
                                                        width: `${bond.ground_truth_validation.accuracy_score * 100}%`,
                                                        height: '100%',
                                                        backgroundColor: bond.ground_truth_validation.accuracy_score >= 0.8 ? '#10b981' :
                                                            bond.ground_truth_validation.accuracy_score >= 0.5 ? '#f59e0b' : '#ef4444',
                                                        transition: 'width 0.3s ease',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        paddingRight: '8px'
                                                    }}
                                                >
                                                    {bond.ground_truth_validation.accuracy_score > 0.15 && (
                                                        <span className="accuracy-progress-text" style={{
                                                            color: 'white',
                                                            fontWeight: '600',
                                                            fontSize: '14px'
                                                        }}>
                                                            {(bond.ground_truth_validation.accuracy_score * 100).toFixed(1)}%
                                                        </span>
                                                    )}
                                                </div>
                                                {bond.ground_truth_validation.accuracy_score <= 0.15 && (
                                                    <span style={{
                                                        position: 'absolute',
                                                        left: `${bond.ground_truth_validation.accuracy_score * 100}%`,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        marginLeft: '8px',
                                                        fontWeight: '600',
                                                        fontSize: '14px',
                                                        color: '#374151'
                                                    }}>
                                                        {(bond.ground_truth_validation.accuracy_score * 100).toFixed(1)}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {bond.ground_truth_validation.message && (
                                    <div className="validation-message" style={{ textAlign: 'left' }}>
                                        <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.validation.message')}:</span>
                                        <span className="detail-value" style={{ textAlign: 'left' }}>{bond.ground_truth_validation.message}</span>
                                    </div>
                                )}

                                {bond.ground_truth_validation.mismatched_fields && bond.ground_truth_validation.mismatched_fields.length > 0 && (
                                    <div className="validation-mismatches" style={{ textAlign: 'left' }}>
                                        <span className="detail-label" style={{ textAlign: 'left' }}>{t('bonds.validation.mismatchedFields')}:</span>
                                        <div className="mismatched-fields-list" style={{ textAlign: 'left' }}>
                                            {bond.ground_truth_validation.mismatched_fields.map((field, idx) => (
                                                <span key={idx} className="field-badge">{field}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {bond.ground_truth_validation.field_validations && (
                                    <div className="field-validations" style={{ marginTop: '24px' }}>
                                        <h4 style={{
                                            color: '#333',
                                            textAlign: 'left',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            marginBottom: '12px'
                                        }}>{t('bonds.validation.fieldValidations', 'Field Validations')}</h4>
                                        <table style={{
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            overflow: 'hidden'
                                        }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f9fafb' }}>
                                                    <th style={{
                                                        padding: '12px',
                                                        textAlign: 'left',
                                                        fontWeight: '600',
                                                        color: '#374151',
                                                        borderBottom: '1px solid #e5e7eb'
                                                    }}>{t('bonds.validation.field', 'Field')}</th>
                                                    <th style={{
                                                        padding: '12px',
                                                        textAlign: 'left',
                                                        fontWeight: '600',
                                                        color: '#374151',
                                                        borderBottom: '1px solid #e5e7eb'
                                                    }}>{t('bonds.validation.bondsValue', 'Bonds Value')}</th>
                                                    <th style={{
                                                        padding: '12px',
                                                        textAlign: 'left',
                                                        fontWeight: '600',
                                                        color: '#374151',
                                                        borderBottom: '1px solid #e5e7eb'
                                                    }}>{t('bonds.validation.groundTruthValue', 'Ground Truth')}</th>
                                                    <th style={{
                                                        padding: '12px',
                                                        textAlign: 'center',
                                                        fontWeight: '600',
                                                        color: '#374151',
                                                        borderBottom: '1px solid #e5e7eb',
                                                        width: '100px'
                                                    }}>{t('bonds.validation.match', 'Match')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(bond.ground_truth_validation.field_validations).map(([field, validation]) => {
                                                    const formatFieldName = (fieldName: string) => {
                                                        return fieldName.split('_').map(word =>
                                                            word.charAt(0).toUpperCase() + word.slice(1)
                                                        ).join(' ');
                                                    };

                                                    const formatValue = (value: any, fieldName: string) => {
                                                        if (value === null || value === undefined) {
                                                            return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>;
                                                        }
                                                        if (fieldName.includes('date') && value) {
                                                            try {
                                                                const date = new Date(value);
                                                                if (!isNaN(date.getTime())) {
                                                                    return formatDate(value);
                                                                }
                                                            } catch { /* ignore parse errors */ }
                                                        }
                                                        if (typeof value === 'boolean') {
                                                            return value ? 'Yes' : 'No';
                                                        }
                                                        if (typeof value === 'number' && fieldName === 'volume') {
                                                            return value.toLocaleString();
                                                        }
                                                        return String(value);
                                                    };

                                                    const isImportantField = ['isin', 'issuer', 'issue_date', 'maturity_date', 'volume', 'currency'].includes(field);
                                                    const isMismatch = !validation.match && validation.has_value;

                                                    return (
                                                        <tr key={field} style={{
                                                            backgroundColor: isMismatch ? '#fef2f2' : 'white'
                                                        }}>
                                                            <td style={{
                                                                padding: '12px',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                fontWeight: isImportantField ? '500' : '400',
                                                                color: '#374151'
                                                            }}>
                                                                {formatFieldName(field)}
                                                                {isImportantField && (
                                                                    <span style={{
                                                                        marginLeft: '4px',
                                                                        fontSize: '10px',
                                                                        color: '#6b7280',
                                                                        fontWeight: '400'
                                                                    }}>★</span>
                                                                )}
                                                            </td>
                                                            <td style={{
                                                                padding: '12px',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                color: isMismatch ? '#991b1b' : '#374151'
                                                            }}>
                                                                {formatValue(validation.bonds_value, field)}
                                                            </td>
                                                            <td style={{
                                                                padding: '12px',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                color: '#374151'
                                                            }}>
                                                                {formatValue(validation.gt_value, field)}
                                                            </td>
                                                            <td style={{
                                                                padding: '12px',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                textAlign: 'center'
                                                            }}>
                                                                <span style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: validation.match ? '#dcfce7' : '#fee2e2',
                                                                    color: validation.match ? '#15803d' : '#991b1b'
                                                                }}>
                                                                    {validation.match ?
                                                                        <i className="fas fa-check" style={{ fontSize: '12px' }}></i> :
                                                                        <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                                                                    }
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div style={{
                                            marginTop: '8px',
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <span>★</span> Key fields for bond identification
                                        </div>
                                    </div>
                                )}

                                {bond.ground_truth_validation.isin_validation && (
                                    <div className="isin-validation" style={{ marginTop: '24px' }}>
                                        <h4 style={{
                                            color: '#333',
                                            textAlign: 'left',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            marginBottom: '12px'
                                        }}>{t('bonds.validation.isinValidation', 'ISIN Validation')}</h4>
                                        <div style={{
                                            padding: '16px',
                                            backgroundColor: bond.ground_truth_validation.isin_validation.valid ? '#f0fdf4' : '#fef2f2',
                                            border: `1px solid ${bond.ground_truth_validation.isin_validation.valid ? '#bbf7d0' : '#fecaca'}`,
                                            borderRadius: '8px'
                                        }}>
                                            <div className="isin-status" style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                marginBottom: bond.ground_truth_validation.isin_validation.errors?.length ? '12px' : '0'
                                            }}>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    backgroundColor: bond.ground_truth_validation.isin_validation.valid ? '#dcfce7' : '#fee2e2',
                                                    color: bond.ground_truth_validation.isin_validation.valid ? '#15803d' : '#991b1b'
                                                }}>
                                                    {bond.ground_truth_validation.isin_validation.valid ?
                                                        <i className="fas fa-check" style={{ fontSize: '16px' }}></i> :
                                                        <i className="fas fa-times" style={{ fontSize: '16px' }}></i>
                                                    }
                                                </span>
                                                <div>
                                                    <div style={{
                                                        fontWeight: '600',
                                                        color: bond.ground_truth_validation.isin_validation.valid ? '#15803d' : '#991b1b'
                                                    }}>
                                                        ISIN {bond.ground_truth_validation.isin_validation.valid ?
                                                            t('bonds.validation.isValid', 'is Valid') :
                                                            t('bonds.validation.isInvalid', 'is Invalid')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        color: '#6b7280',
                                                        marginTop: '2px'
                                                    }}>
                                                        {bond.isin}
                                                    </div>
                                                </div>
                                            </div>
                                            {bond.ground_truth_validation.isin_validation.errors && bond.ground_truth_validation.isin_validation.errors.length > 0 && (
                                                <div className="validation-errors">
                                                    <div style={{
                                                        fontWeight: '500',
                                                        color: '#991b1b',
                                                        marginBottom: '8px',
                                                        fontSize: '14px'
                                                    }}>{t('bonds.validation.errors', 'Validation Errors')}:</div>
                                                    <ul style={{
                                                        margin: '0',
                                                        paddingLeft: '20px',
                                                        color: '#991b1b'
                                                    }}>
                                                        {bond.ground_truth_validation.isin_validation.errors.map((error, idx) => (
                                                            <li key={idx} style={{ fontSize: '14px', marginBottom: '4px' }}>{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {bond.ground_truth_validation.isin_validation.warnings && bond.ground_truth_validation.isin_validation.warnings.length > 0 && (
                                                <div className="validation-warnings" style={{ marginTop: '12px' }}>
                                                    <div style={{
                                                        fontWeight: '500',
                                                        color: '#92400e',
                                                        marginBottom: '8px',
                                                        fontSize: '14px'
                                                    }}>{t('bonds.validation.warnings', 'Warnings')}:</div>
                                                    <ul style={{
                                                        margin: '0',
                                                        paddingLeft: '20px',
                                                        color: '#92400e'
                                                    }}>
                                                        {bond.ground_truth_validation.isin_validation.warnings.map((warning, idx) => (
                                                            <li key={idx} style={{ fontSize: '14px', marginBottom: '4px' }}>{warning}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="validation-timestamps" style={{
                                    marginTop: '24px',
                                    padding: '16px',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: bond.ground_truth_cross_referenced_at ? '12px' : '0'
                                    }}>
                                        <i className="fas fa-calendar-check" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                        <span style={{ fontWeight: '500', color: '#374151' }}>
                                            {t('bonds.validation.validatedAt', 'Validated At')}:
                                        </span>
                                        <span style={{ color: '#6b7280' }}>
                                            {formatDate(bond.ground_truth_validation.validated_at)}
                                        </span>
                                    </div>
                                    {bond.ground_truth_cross_referenced_at && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <i className="fas fa-link" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                            <span style={{ fontWeight: '500', color: '#374151' }}>
                                                {t('bonds.validation.crossReferencedAt', 'Cross-Referenced At')}:
                                            </span>
                                            <span style={{ color: '#6b7280' }}>
                                                {formatDate(bond.ground_truth_cross_referenced_at)}
                                            </span>
                                        </div>
                                    )}
                                    {bond.isin_validation_updated_at && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginTop: '12px'
                                        }}>
                                            <i className="fas fa-sync-alt" style={{ color: '#6b7280', fontSize: '14px' }}></i>
                                            <span style={{ fontWeight: '500', color: '#374151' }}>
                                                {t('bonds.validation.isinUpdatedAt', 'ISIN Validation Updated')}:
                                            </span>
                                            <span style={{ color: '#6b7280' }}>
                                                {formatDate(bond.isin_validation_updated_at)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}


                    {/* Optimization Information - Admin Only */}
                    {user?.role === 'ADMIN' && bond.optimization && (
                        <div className="detail-section full-width">
                            <h3>{t('bonds.optimizationInformation', 'Optimization Information')}</h3>

                            <div className="optimization-overview">
                                <div className="detail-row">
                                    <span className="detail-label">{t('bonds.optimization.status', 'Status')}:</span>
                                    <span className={`validation-badge ${bond.optimization.optimized ? 'valid' : 'invalid'}`}>
                                        {bond.optimization.optimized ? t('bonds.optimization.optimized', 'Optimized') : t('bonds.optimization.notOptimized', 'Not Optimized')}
                                    </span>
                                </div>

                                {bond.optimization.optimization_version && (
                                    <div className="detail-row">
                                        <span className="detail-label">{t('bonds.optimization.version', 'Version')}:</span>
                                        <span className="detail-value">{bond.optimization.optimization_version}</span>
                                    </div>
                                )}

                                {bond.optimization.optimization_source && (
                                    <div className="detail-row">
                                        <span className="detail-label">{t('bonds.optimization.source', 'Source')}:</span>
                                        <span className="detail-value">{bond.optimization.optimization_source}</span>
                                    </div>
                                )}

                                {bond.optimization.parsed_fields && bond.optimization.parsed_fields.length > 0 && (
                                    <div className="detail-row">
                                        <span className="detail-label">{t('bonds.optimization.parsedFields', 'Parsed Fields')} ({bond.optimization.parsed_fields_count}):</span>
                                        <div className="parsed-fields-list" style={{ marginTop: '8px' }}>
                                            {bond.optimization.parsed_fields.map((field, idx) => (
                                                <span key={idx} className="field-badge" style={{
                                                    display: 'inline-block',
                                                    padding: '4px 8px',
                                                    margin: '2px 4px',
                                                    backgroundColor: '#e0f2fe',
                                                    color: '#0369a1',
                                                    borderRadius: '4px',
                                                    fontSize: '12px'
                                                }}>{field}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="detail-row">
                                    <span className="detail-label">{t('bonds.optimization.optimizationCount', 'Optimization Count')}:</span>
                                    <span className="detail-value">{bond.optimization.optimization_count}</span>
                                </div>

                                <div className="optimization-timestamps">
                                    <div className="detail-row">
                                        <span className="detail-label">{t('bonds.optimization.optimizedAt', 'Optimized At')}:</span>
                                        <span className="detail-value">{formatDate(bond.optimization.optimized_at)}</span>
                                    </div>
                                    {bond.optimization.previous_optimized_at && (
                                        <div className="detail-row">
                                            <span className="detail-label">{t('bonds.optimization.previouslyOptimizedAt', 'Previously Optimized At')}:</span>
                                            <span className="detail-value">{formatDate(bond.optimization.previous_optimized_at)}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Optimization History */}
                                {bond.optimization_history && bond.optimization_history.length > 0 && (
                                    <div className="optimization-history" style={{ marginTop: '16px' }}>
                                        <h4>{t('bonds.optimization.history', 'Optimization History')}</h4>
                                        <div style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '4px',
                                            padding: '8px'
                                        }}>
                                            {bond.optimization_history.map((history, idx) => (
                                                <div key={idx} style={{
                                                    padding: '8px',
                                                    borderBottom: idx < bond.optimization_history!.length - 1 ? '1px solid #f3f4f6' : 'none'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                        <span style={{ fontWeight: '500' }}>
                                                            {formatDate(history.optimized_at)}
                                                        </span>
                                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            v{history.optimization_version} | Count: {history.optimization_count}
                                                        </span>
                                                    </div>
                                                    {history.parsed_fields_count && (
                                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {history.parsed_fields_count} fields parsed
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Footer - Last Updated Information */}
                    <div className="detail-section full-width" style={{ backgroundColor: '#ffffff', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div className="update-info">
                                <i className="fas fa-clock"></i>
                                {t('bonds.lastUpdated')}: {formatDate(bond.update_timestamp)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BondDetailView;